import { useEffect, useRef, useState } from 'react';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';

const SUGGEST_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest';
const FIND_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

type Suggestion = { text: string; magicKey: string };

export type ProfileLocationDraft = {
  label: string;
  latitude: string;
  longitude: string;
};

type Props = {
  value: ProfileLocationDraft;
  onChange: (value: ProfileLocationDraft) => void;
};

/**
 * Address search and lat/long edit the same point. Picking an address fills the
 * coordinates; typing coordinates keeps whatever address text is showing.
 */
const ProfileCurrentLocation: React.FC<Props> = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const trimmed = value.label.trim();

  useEffect(() => {
    if (!addressFocused || trimmed.length < 3) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const params = new URLSearchParams({
        f: 'json',
        text: trimmed,
        maxSuggestions: '5',
      });
      void fetch(`${SUGGEST_URL}?${params.toString()}`)
        .then((response) => response.json())
        .then((body: { suggestions?: Suggestion[] }) => {
          if (cancelled) return;
          setSuggestions(body.suggestions ?? []);
          setMenuOpen(true);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [addressFocused, trimmed]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const chooseSuggestion = async (suggestion: Suggestion) => {
    setMenuOpen(false);
    const params = new URLSearchParams({
      f: 'json',
      SingleLine: suggestion.text,
      magicKey: suggestion.magicKey,
      maxLocations: '1',
    });
    const response = await fetch(`${FIND_URL}?${params.toString()}`);
    const body = (await response.json()) as {
      candidates?: { location?: { x: number; y: number } }[];
    };
    const location = body.candidates?.[0]?.location;
    if (!location) {
      onChange({ ...value, label: suggestion.text });
      return;
    }
    onChange({
      label: suggestion.text,
      latitude: String(location.y),
      longitude: String(location.x),
    });
  };

  return (
    <div className="col-12 mb-3" ref={rootRef}>
      <label className="form-label">Current Location</label>
      <p className="tw:mb-2 tw:text-xs tw:text-muted-foreground">
        An address or a latitude and longitude. A notification filtered by a
        custom location uses this point, not your home area of responsibility.
      </p>
      <StylishNewInput
        value={value.label}
        placeholder="Search an address"
        onChange={(event) => onChange({ ...value, label: event.target.value })}
        onFocus={() => {
          setAddressFocused(true);
          if (suggestions.length > 0) setMenuOpen(true);
        }}
        onBlur={() => setAddressFocused(false)}
        autoComplete="off"
      />
      {menuOpen && suggestions.length > 0 ? (
        <div className="tw:mt-1 tw:rounded-md tw:border tw:border-border tw:bg-background">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.magicKey}
              type="button"
              className="tw:block tw:w-full tw:px-3 tw:py-2 tw:text-left tw:text-sm tw:hover:bg-muted"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void chooseSuggestion(suggestion)}
            >
              {suggestion.text}
            </button>
          ))}
        </div>
      ) : null}
      <div className="row mt-2">
        <div className="col-md-6">
          <label className="form-label">Latitude</label>
          <StylishNewInput
            value={value.latitude}
            placeholder="Latitude"
            onChange={(event) =>
              onChange({ ...value, latitude: event.target.value })
            }
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Longitude</label>
          <StylishNewInput
            value={value.longitude}
            placeholder="Longitude"
            onChange={(event) =>
              onChange({ ...value, longitude: event.target.value })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileCurrentLocation;
